import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import AcaoModal from './components/modals/AcaoModal';
import usePagination10 from './components/hooks/usePagination10';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = { idPrograma: '', id: '', descricao: '' };

function Acoes() {
  const token = localStorage.getItem('token');
  const [items, setItems] = useState([]);
  const [programas, setProgramas] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [filtroPrograma, setFiltroPrograma] = useState("");

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  // Filtra ações pelo nome do programa
  const itemsFiltrados = filtroPrograma
    ? items.filter(item => String(item.idPrograma) === String(filtroPrograma))
    : items;

  const { setPagination } = usePagination10(itemsFiltrados.length);
  // Ordena as ações em ordem alfabética pela descrição
  const displayed = itemsFiltrados
    .sort((a, b) => a.descricao.localeCompare(b.descricao))
    .slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    setIsLoading(true);
    Promise.all([
      axios.get(`${API_BASE_URL.ACAO}/all`, { headers: h }),
      axios.get(`${API_BASE_URL.PROGRAMA}/all`, { headers: h }),
    ]).then(([rAcoes, rProgramas]) => {
      const sorted = [...rAcoes.data].sort((a, b) =>
        a.idPrograma !== b.idPrograma ? a.idPrograma - b.idPrograma : a.id - b.id
      );
      setItems(sorted);
      setProgramas(rProgramas.data);
    }).catch(err => {
      if (err.response && err.response.status !== 200 && err.response.status !== 404) {
        showDefaultModal('error', 'Erro ao carregar ações.');
      }
    })
      .finally(() => setIsLoading(false));
  }, [token, atualizar]);

  function handleChange(e) { const { name, value } = e.target; setForm(f => ({ ...f, [name]: value })); }
  function handleNew() { setForm(emptyForm); setShowModal(true); }
  function handleEdit() { if (selected.length !== 1) return; setForm({ ...selected[0] }); setShowModal(true); }

  function handleSave(e) {
    e.preventDefault();
    axios.post(`${API_BASE_URL.ACAO}/save`, form, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]); // Limpa seleção após salvar
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar ação.'));
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s => axios.delete(`${API_BASE_URL.ACAO}/delete`, {
      params: { idPrograma: s.idPrograma, id: s.id },
      headers: { Authorization: `Bearer ${token}` }
    }))).then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir ação.'))
    );
  }

  function keyOf(item) { return `${item.idPrograma}_${item.id}`; }
  function toggleSelect(item) {
    setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]);
  }

  // Agrupa os itens exibidos por programa, preservando a ordem
  const grouped = programas
    .filter(p => displayed.some(item => String(item.idPrograma) === String(p.id)))
    .map(p => ({
      programa: p,
      acoes: displayed.filter(item => String(item.idPrograma) === String(p.id)),
    }));

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="AÇÕES" icon="bi-lightning" />
        <PageContent>
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0}>
            <div className="toolbar-search">
              <select className="form-select form-select-sm" value={filtroPrograma} onChange={e => setFiltroPrograma(e.target.value)}>
                <option value="">Todos os programas</option>
                {programas.map(p => <option key={p.id} value={p.id}>{p.descricao}</option>)}
              </select>
            </div>

          </Toolbar>
          <TableContainer isLoading={false}>
                              <DataTable head={<tr>
                  <th style={{ width: '3rem' }}>#</th><th>Descrição</th>
                </tr>}>
                <tbody>
                  {grouped.map(group => (
                    <React.Fragment key={`prog_${group.programa.id}`}>
                      <tr style={{ backgroundColor: '#d1e7dd', pointerEvents: 'none' }}>
                        <td colSpan={3} className="fw-semibold small">
                          <i className="bi bi-diagram-3 me-2 text-success"></i>
                          {group.programa.descricao}
                        </td>
                      </tr>
                      {group.acoes.map((item, idx) => (
                        <tr key={keyOf(item)} className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                          <td className="text-muted" style={{ paddingLeft: '2rem' }}>{idx + 1}</td>
                          <td>{item.descricao}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <AcaoModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} programas={programas} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Acoes;
